/**
 * v2 theme provider — light / dark / system.
 *
 * Stored in localStorage under ``chassis_ui_theme``. The provider
 * applies ``class="dark"`` to the document root when dark mode
 * is active so Tailwind's ``dark:*`` variants take effect.
 *
 * ``"system"`` reads ``window.matchMedia("(prefers-color-scheme: dark)")``
 * and updates live if the OS-level pref changes mid-session.
 *
 * v1 pages render unchanged because their hardcoded ``slate-*`` /
 * ``bg-slate-900`` utilities aren't gated on ``dark:``. Only v2
 * components that explicitly use ``dark:`` see the toggle.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ThemeChoice = "light" | "dark" | "system";

const STORAGE_KEY = "chassis_ui_theme";
const DEFAULT_THEME: ThemeChoice = "system";

type ThemeContextValue = {
  theme: ThemeChoice;
  /** The currently rendered theme — "system" resolves to light or dark here. */
  resolved: "light" | "dark";
  setTheme: (next: ThemeChoice) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStored(): ThemeChoice {
  if (typeof window === "undefined") return DEFAULT_THEME;
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === "light" || v === "dark" || v === "system") return v;
  return DEFAULT_THEME;
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return true;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function applyClass(resolved: "light" | "dark") {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeChoice>(readStored);
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  // React to OS-level pref changes when in "system" mode.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    // Safari fallback.
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  const resolved: "light" | "dark" = useMemo(() => {
    if (theme === "system") return systemDark ? "dark" : "light";
    return theme;
  }, [theme, systemDark]);

  // Mirror the resolved theme to the <html> class so Tailwind's
  // ``dark:*`` variants react immediately.
  useEffect(() => {
    applyClass(resolved);
  }, [resolved]);

  const setTheme = useCallback((next: ThemeChoice) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage may be unavailable in private-browse or sandboxed
      // contexts; the in-memory state still works for the session.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolved, setTheme }),
    [theme, resolved, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used inside a <ThemeProvider>");
  }
  return ctx;
}
